// HR › Payroll review: one employee's rest-day pay and night shift differential (Report 55), and the dialog in which a
// payroll manager enters the amount for a day Apparify could not work out alone. The server decides every amount,
// every "needs a decision" and every "not owed"; this only shows them and sends the review.
import { useId, useState } from "react";

import { Button, Dialog, Field, Input, Money, Notice, Status, Textarea, formatMoney, plainError } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";

export const PREMIUM_LABEL = { REST_DAY: "Rest-day pay", NIGHT_DIFFERENTIAL: "Night shift differential" };
const DAY_KIND = { ORDINARY: "an ordinary day", REST: "a rest day", HOLIDAY: "a holiday" };
const REASON_MAX = 500;
const MAX_CENTS = 100_000_000;

const dateText = (date) => new Date(`${date}T00:00:00`).toLocaleDateString("en-PH", { weekday: "short", month: "short", day: "numeric" });
const hours = (minutes) => {
  const total = Number(minutes || 0);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return [h ? `${h} h` : null, m || !h ? `${m} min` : null].filter(Boolean).join(" ");
};

/** The plain word for a line's state, and its tone. Status is never colour alone. */
export function premiumStateView(line) {
  switch (line.state) {
    case "AUTO_CALCULATED": return { word: "Calculated", tone: "neutral" };
    case "MANUALLY_REVIEWED": return { word: "Reviewed by HR", tone: "neutral" };
    case "NOT_APPLICABLE": return { word: "Not owed", tone: "neutral" };
    default: return { word: "Needs a decision", tone: "warning" };
  }
}

/** What the line is about, in one line: the time that earned it. */
function premiumFacts(line) {
  if (line.component === "NIGHT_DIFFERENTIAL") {
    return line.nightMinutes != null ? `${hours(line.nightMinutes)} between 10 p.m. and 6 a.m. on ${DAY_KIND[line.dayKind] || "the day"}` : "Night work";
  }
  const total = Number(line.workedMinutes || 0) + Number(line.overtimeMinutes || 0);
  return `${hours(total)} worked on a rest day${Number(line.overtimeMinutes) ? `, ${hours(line.overtimeMinutes)} of it approved overtime` : ""}`;
}

/** Validates the amount (₱, up to two decimals) and the reason. Returns { errors, cents, reason }. */
export function premiumReviewErrors(form) {
  const errors = {};
  const raw = String(form.amount ?? "").trim().replace(/,/g, "");
  let cents = null;
  if (!raw) errors.amount = "Enter the amount, ₱0.00 or more.";
  else if (!/^\d+(\.\d{1,2})?$/.test(raw)) errors.amount = "Enter an amount like 180.00.";
  else {
    cents = Math.round(Number(raw) * 100);
    if (cents > MAX_CENTS) errors.amount = "That amount is too large.";
  }
  const reason = String(form.reason ?? "").trim();
  if (reason.length < 5) errors.reason = "Say how you arrived at this amount (at least 5 characters).";
  else if (reason.length > REASON_MAX) errors.reason = `Keep the reason under ${REASON_MAX} characters.`;
  return { errors, cents, reason };
}

export default function PayrollPremiumLines({ member, lines, status, canManage, onReview }) {
  if (!lines?.length) return null;
  const canReview = canManage && status === "computed";
  return (
    <section aria-label={`Rest-day and night pay for ${member.name}`}>
      <h4 className="mb-1 text-label text-foreground">Rest-day and night pay</h4>
      <ul className="divide-y divide-subtle text-body-sm">
        {lines.map((line) => {
          const state = premiumStateView(line);
          const waiting = line.state === "REVIEW_REQUIRED";
          return (
            <li key={`${line.date}-${line.component}`} id={`premium-${member.memberId}-${line.date}-${line.component}`} className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="min-w-0">
                <p className="text-foreground">
                  <span className="font-medium">{PREMIUM_LABEL[line.component]}</span>
                  <span className="text-secondary"> · {dateText(line.date)}</span>
                </p>
                <p className="text-meta text-secondary">{premiumFacts(line)}</p>
                {line.state === "NOT_APPLICABLE" && line.coverageText ? <p className="text-meta text-secondary">{line.coverageText}</p> : null}
                {waiting ? <p className="mt-0.5 text-meta text-secondary">{line.coverageText || line.reasonText}</p> : null}
                {line.review?.reason ? <p className="text-meta text-tertiary">Reviewed: {line.review.reason}</p> : null}
              </div>
              <div className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
                {waiting
                  ? <Status tone="warning" icon>{state.word}</Status>
                  : <span className="text-meta text-secondary">{state.word}</span>}
                {line.amountCents != null ? <Money value={line.amountCents} className="text-money text-foreground" /> : null}
                {waiting && line.referenceCents != null ? <span className="text-meta text-secondary">For reference: <span className="tabular-nums">{formatMoney(line.referenceCents)}</span></span> : null}
                {canReview && line.canReview ? (
                  <Button size="sm" onClick={() => onReview(line)}>{line.state === "MANUALLY_REVIEWED" ? "Change amount" : "Enter amount"}</Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * The amount and reason for one rest-day or night pay day. A refusal stays in the dialog in the server's words; a
 * payroll recalculated in the meantime is said, and the dialog offers a refresh instead of resending.
 */
export function PremiumReviewDialog({ periodId, member, line, onSaved, onClose, onStale }) {
  const formId = useId();
  const [form, setForm] = useState({
    amount: line.state === "MANUALLY_REVIEWED" && line.amountCents != null ? (line.amountCents / 100).toFixed(2) : "",
    reason: "",
  });
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setFailure("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving || stale) return;
    const checked = premiumReviewErrors(form);
    setErrors(checked.errors);
    const first = ["amount", "reason"].find((key) => checked.errors[key]);
    if (first) {
      document.getElementById(`${formId}-${first}`)?.focus();
      return;
    }
    setSaving(true);
    try {
      await businessApi.recordPayrollPremiumReview(periodId, {
        memberId: member.memberId, date: line.date, component: line.component, amountCents: checked.cents, reason: checked.reason,
      });
      onSaved(line);
      return;
    } catch (err) {
      const code = err?.serverCode;
      if (err?.status === 404 || ["PREMIUM_REVIEW_STALE", "PREMIUM_REVIEW_NOT_OPEN", "PREMIUM_REVIEW_NOT_NEEDED"].includes(code)) setStale(true);
      else setFailure(err?.status === 403 ? "You can't enter a reviewed amount. Ask someone who manages payroll." : plainError(err, "The amount was not saved. Try again."));
    }
    setSaving(false);
  };

  const facts = [
    ["Employee", member.name],
    ["Pay", PREMIUM_LABEL[line.component]],
    ["Date", dateText(line.date)],
    ["Work", premiumFacts(line)],
  ];

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !saving) (stale ? onStale : onClose)(); }}
      title={`Decide ${PREMIUM_LABEL[line.component].toLowerCase()} for ${member.name}`}
      description={dateText(line.date)}
      footer={stale ? <Button variant="primary" onClick={onStale}>Refresh</Button> : (
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={saving}>Save amount</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-4">
        <dl className="divide-y divide-subtle border-y border-subtle text-body-sm">
          {facts.map(([label, value]) => (
            <div key={label} className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
              <dt className="shrink-0 text-secondary">{label}</dt>
              <dd className="min-w-0 break-words text-foreground sm:text-right">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="text-body-sm">
          <p className="text-label text-foreground">Why a person decides this day</p>
          <p className="mt-0.5 text-secondary">{line.coverageText || line.reasonText}</p>
        </div>
        {stale ? (
          <Notice tone="warning" title="Out of date" role="alert">The payroll changed since this screen was opened. Refresh to see the current figures, then decide again.</Notice>
        ) : (
          <>
            {line.referenceCents != null ? (
              <div className="flex flex-col gap-2 rounded-control bg-surface-sunken px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-body-sm text-secondary">
                  What the rule would give: <span className="tabular-nums text-foreground">{formatMoney(line.referenceCents)}</span>. You decide the amount.
                </p>
                <Button size="sm" disabled={saving} className="shrink-0"
                  onClick={() => { setForm((current) => ({ ...current, amount: (line.referenceCents / 100).toFixed(2) })); setErrors((current) => ({ ...current, amount: undefined })); }}>
                  Use this amount
                </Button>
              </div>
            ) : <p className="text-body-sm text-secondary">Apparify has no reference amount for this day.</p>}
            <Field label="Amount added for this day (₱)" required id={`${formId}-amount`} error={errors.amount}
              hint="Only what this day adds on top of basic pay and overtime already paid.">
              <Input inputMode="decimal" autoComplete="off" maxLength={14} placeholder="0.00" value={form.amount} onChange={set("amount")} disabled={saving} className="tabular-nums" />
            </Field>
            <Field label="Reason" required id={`${formId}-reason`} error={errors.reason} hint="How you arrived at this amount. Finance sees this.">
              <Textarea rows={3} maxLength={REASON_MAX} value={form.reason} onChange={set("reason")} disabled={saving} />
            </Field>
            {failure ? <Notice tone="danger" title="Not saved" role="alert">{failure}</Notice> : null}
          </>
        )}
      </form>
    </Dialog>
  );
}
